import type { Icon as TablerIcon } from "@tabler/icons-react";
import type { CreferenceEntityType } from "./CreferenceEntity";
import {
  normalizeEntityId,
  selectCreferenceProductIcon,
  type ReferenceCollab,
  type ReferenceFile,
  type ReferenceMember,
  type ReferenceProduct,
  type ReferenceVersion,
} from "./creferenceResolvedEntitySelectors";
import { useCreferenceCurrentUserFields } from "./useCreferenceCurrentUserFields";
import { useCreferenceLabelState } from "./useCreferenceLabelState";
import { useCreferenceMemberProviderFields } from "./useCreferenceMemberProviderFields";
import { useCreferenceResolvedLookup } from "./useCreferenceResolvedLookup";

interface CreferenceResolvedEntityInput {
  entityId: string;
}

export interface CreferenceResolvedEntity {
  collab: ReferenceCollab | null;
  currentUserImageSrc: string | null;
  file: ReferenceFile | null;
  effectiveEntityType: CreferenceEntityType;
  isCurrentUserReference: boolean;
  isLoading: boolean;
  isUnresolved: boolean;
  label: string;
  /** Version marker appended AFTER the label's truncation — see
   *  `CreferenceLabelParts`. Null for every label that is a real name. */
  labelSuffix: string | null;
  member: ReferenceMember | null;
  memberProviderIconPath: string;
  memberProviderSlug: string;
  normalizedEntityId: string;
  product: ReferenceProduct | null;
  productIcon: TablerIcon | null;
  version: ReferenceVersion | null;
}

export function useCreferenceResolvedEntity({
  entityId,
}: CreferenceResolvedEntityInput): CreferenceResolvedEntity {
  const normalizedEntityId = normalizeEntityId(entityId);
  const resolvedLookup = useCreferenceResolvedLookup({ normalizedEntityId });
  const {
    collab,
    file,
    effectiveEntityType,
    isCurrentUserReference,
    member,
    product,
    version,
    versionProduct,
    sessionLoading,
  } = resolvedLookup;
  const { currentUserImageSrc, currentUserName, profileLoading } =
    useCreferenceCurrentUserFields({ isCurrentUserReference });
  const { memberProviderIconPath, memberProviderSlug } =
    useCreferenceMemberProviderFields({ member });
  const { isLoading, isUnresolved, label, labelSuffix } = useCreferenceLabelState({
    collab,
    currentUserName,
    effectiveEntityType,
    file,
    isCurrentUserReference,
    member,
    product,
    profileLoading,
    sessionLoading,
    version,
    versionProduct,
  });

  return {
    collab,
    currentUserImageSrc,
    file,
    effectiveEntityType,
    isCurrentUserReference,
    isLoading,
    isUnresolved,
    label,
    labelSuffix,
    member,
    memberProviderIconPath,
    memberProviderSlug,
    normalizedEntityId,
    product,
    productIcon: selectCreferenceProductIcon(product),
    version,
  };
}
